'use client'

import { useSocketConnection } from '@/hooks/socket/useSocketConnection'
import { useChatStore } from '@/store/chat.store'
import { resolveTicketFollowUpSend } from '@/lib/ticket-follow-up'
import { shouldAcknowledgeTicketUpdates } from '@/lib/ticket-update-badge'
import { usePersistStore } from '@/store/persist.store'
import { useSocketStore } from '@/store/socket.store'
import { createContext, ReactNode, RefObject, useContext, useEffect, useRef, useState } from 'react'
import { useTranslations } from 'next-intl'
import { useChatContext } from './ChatProvider'

interface SocketContextType {
  wsRef: RefObject<WebSocket | null>
  sendFrame: (frame: string) => Promise<void> | undefined
  canSend: () => boolean
  endCurrentSession: () => Promise<void>
  closeChat: () => Promise<void>
  isClosingChat: boolean
  connectWebSocket: () => void
  requestMessageHistory: (page: number, pageSize?: number) => void
  handleSelectSubject: (section: string, label: string) => void
  requestTickets: () => void
  requestTicketHistory: (ticketId: string, options?: { silent?: boolean }) => void
  sendTicketFollowUp: (ticketId: string, content: string) => void
  markTicketUpdatesRead: (cursor: string) => void
}

export const SocketContext = createContext<SocketContextType | undefined>(undefined)

export default function SocketProvider({ children }: { children: ReactNode }) {
  const t = useTranslations('chat')
  const token = usePersistStore(state => state.token)
  const siteId = usePersistStore(state => state.siteId)
  const socketConnectionStatus = useSocketStore(state => state.socketConnectionStatus)

  // Get values from chat store
  const setMessages = useChatStore(state => state.setMessages)
  const setIsLoadingHistory = useChatStore(state => state.setIsLoadingHistory)
  const isFirstButtonClick = useChatStore(state => state.isFirstButtonClick)
  const activeTab = useChatStore(state => state.activeTab)
  const ticketUpdateCount = useChatStore(state => state.ticketUpdateCount)
  const ticketUpdateCursor = useChatStore(state => state.ticketUpdateCursor)
  const setSelectedSection = useChatStore(state => state.setSelectedSection)
  const setIsShowSectionSelectButtons = useChatStore(state => state.setIsShowSectionSelectButtons)

  // Get scroll functionality from context
  const { scrollToBottom } = useChatContext()

  // Use socket connection hook
  const { wsRef, connectWebSocket, disconnectWebSocket, sendFrame, canSend } = useSocketConnection()

  const closingRef = useRef(false)
  const [isClosingChat, setIsClosingChat] = useState(false)

  // new_topic permanently closes the current session and acknowledges it.
  // Reconnection is a separate, explicit action used only by refresh.
  const endCurrentSession = async () => {
    if (!usePersistStore.getState().sessionId) return
    if (!canSend() || useSocketStore.getState().pendingNewTopicAck) {
      throw new Error(t('connectionFailed'))
    }
    let timeout: ReturnType<typeof setTimeout> | undefined
    const acknowledgement = new Promise<void>((resolve, reject) => {
      useSocketStore.getState().setPendingNewTopicAck({ resolve, reject })
      timeout = setTimeout(() => reject(new Error(t('connectionFailed'))), 15000)
    })
    try {
      // Observe the acknowledgement immediately, including while migration queues the send.
      await Promise.all([sendFrame(JSON.stringify({ type: 'new_topic' })), acknowledgement])
    } finally {
      clearTimeout(timeout)
      useSocketStore.getState().setPendingNewTopicAck(null)
    }
  }

  const closeChat = async () => {
    if (closingRef.current || useSocketStore.getState().pendingNewTopicAck) return
    closingRef.current = true
    setIsClosingChat(true)
    try {
      await endCurrentSession()
      disconnectWebSocket()
      usePersistStore.setState({ sessionId: null, isTakenOver: false })
      useChatStore.setState({
        isChatEnded: true,
        selectedSection: null,
        isShowSectionSelectButtons: true,
        operatorName: null,
        liveSupportState: null,
        isSecondChatShown: false,
        activeTab: 'chat',
        messages: [],
        pendingReview: null
      })
      window.parent.postMessage({ type: 'TOGGLE_IS_OPEN', payload: { isChatIframeShown: false } }, '*')
    } catch (error) {
      useChatStore.getState().setActiveTab('chat')
      setMessages(previous => [
        ...previous,
        {
          role: 'assistant',
          content: error instanceof Error ? error.message : t('connectionFailed'),
          type: 'error'
        }
      ])
      scrollToBottom()
    } finally {
      closingRef.current = false
      setIsClosingChat(false)
    }
  }

  const requestMessageHistory = (page: number, pageSize: number = 30) => {
    if (canSend()) {
      setIsLoadingHistory(true)
      sendFrame(
        JSON.stringify({
          type: 'message_history',
          page_size: pageSize.toString(),
          page: page.toString()
        })
      )
    } else {
      console.warn('WebSocket is not open.')
    }
  }

  const requestTickets = () => {
    if (canSend()) {
      useChatStore.getState().setIsLoadingTickets(true)
      sendFrame(JSON.stringify({ type: 'get_tickets' }))
    } else {
      console.warn('WebSocket is not open.')
    }
  }

  const requestTicketHistory = (ticketId: string, options?: { silent?: boolean }) => {
    if (canSend()) {
      // Silent refreshes re-fetch an already-open ticket without blanking it
      // behind the loading state.
      if (!options?.silent) {
        const { setIsLoadingTicketHistory, setTicketHistoryError } = useChatStore.getState()
        setTicketHistoryError(false)
        setIsLoadingTicketHistory(true)
      }
      sendFrame(JSON.stringify({ type: 'get_ticket_history', ticket_id: ticketId }))
    } else {
      console.warn('WebSocket is not open.')
    }
  }

  const sendTicketFollowUp = (ticketId: string, content: string) => {
    const socketOpen = Boolean(canSend())
    const resolution = resolveTicketFollowUpSend(socketOpen)
    const { setIsSendingTicketFollowUp, setTicketFollowUpError } = useChatStore.getState()

    if (resolution.clearError) {
      setTicketFollowUpError(false)
    }
    if (resolution.markSending) {
      setIsSendingTicketFollowUp(true)
    }
    if (resolution.markError) {
      setTicketFollowUpError(true)
      setIsSendingTicketFollowUp(false)
    }

    if (resolution.canSend) {
      sendFrame(JSON.stringify({ type: 'ticket_follow_up', ticket_id: ticketId, content }))
    } else {
      console.warn('WebSocket is not open.')
    }
  }

  const markTicketUpdatesRead = (cursor: string) => {
    if (!cursor || !canSend()) return
    sendFrame(JSON.stringify({ type: 'ticket_updates_read', cursor }))
    useChatStore.getState().setTicketUpdateBadge(0, cursor)
  }

  const handleSelectSubject = (section: string, label: string) => {
    setSelectedSection({ section, label })
    setIsShowSectionSelectButtons(false)

    if (canSend()) {
      sendFrame(
        JSON.stringify({
          type: 'section_select',
          section,
          label,
          message: t('subjectSelected', { label })
        })
      )

      // Append message to chat
      setMessages(prev => [
        ...prev,
        {
          role: 'system',
          content: t('subjectSelected', { label }),
          type: 'notification'
        }
      ])
      scrollToBottom({ behavior: 'instant' })
    } else {
      console.warn('WebSocket is not open.')
    }
  }

  useEffect(() => {
    // The initial connection is user-triggered. Persisted credentials may be
    // prepared before the widget opens, but must not create a socket by themselves.
    if (isFirstButtonClick) {
      connectWebSocket()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, siteId, isFirstButtonClick])

  useEffect(() => {
    if (
      socketConnectionStatus === 'opened' &&
      shouldAcknowledgeTicketUpdates(activeTab, ticketUpdateCount, ticketUpdateCursor)
    ) {
      markTicketUpdatesRead(ticketUpdateCursor)
    }
    // Connection state is a trigger so a read attempted while disconnected is
    // retried as soon as the socket opens again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, ticketUpdateCount, ticketUpdateCursor, socketConnectionStatus])

  useEffect(() => {
    function handleContentWindowMessage(event: MessageEvent) {
      if (typeof event.data !== 'object' || !event.data.type) return

      const { type, payload } = event.data

      if (type === 'SELECTED_SUBJECT') {
        handleSelectSubject(payload?.section, payload?.label)
      }
    }

    window.addEventListener('message', handleContentWindowMessage)

    return () => window.removeEventListener('message', handleContentWindowMessage)
  }, [])

  return (
    <SocketContext.Provider
      value={{
        wsRef,
        sendFrame,
        canSend,
        connectWebSocket,
        endCurrentSession,
        closeChat,
        isClosingChat,
        requestMessageHistory,
        handleSelectSubject,
        requestTickets,
        requestTicketHistory,
        sendTicketFollowUp,
        markTicketUpdatesRead
      }}
    >
      {children}
    </SocketContext.Provider>
  )
}

export function useSocketContext() {
  const context = useContext(SocketContext)
  if (!context) {
    throw new Error('useSocketContext must be used within a SocketProvider')
  }

  return context
}
